import React, { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import dayjs from "dayjs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const BALANCE_COLORS = {
  Annual: "#00ff90", Sick: "#f87171", Casual: "#c084fc",
  Unpaid: "#fbbf24", Maternity: "#60a5fa", Paternity: "#34d399",
};

const STATUS_STYLE = {
  APPROVED:   "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  PENDING:    "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  REJECTED:   "text-red-400 bg-red-500/10 border-red-500/20",
  OVERRIDDEN: "text-orange-400 bg-orange-500/10 border-orange-500/20",
  CANCELLED:  "text-white/25 bg-white/5 border-white/10",
};

const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

export default function EmployeeLeaveDrawer({ employeeId, employeeName, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("balances"); // balances | history | entitlement

  // Add leave form
  const [showAdd, setShowAdd]  = useState(false);
  const [addForm, setAddForm]  = useState({ leave_type: "Annual", from: "", to: "", reason: "" });
  const [addBusy, setAddBusy]  = useState(false);
  const [addMsg,  setAddMsg]   = useState(null);

  // Entitlement override
  const [overrides, setOverrides] = useState({});
  const [savingOvr, setSavingOvr] = useState(false);
  const [ovrMsg,    setOvrMsg]    = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    fetch(`${API_BASE}/api/admin/leave-rules/employee/${employeeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setData(d); setOverrides(d?.entitlement_overrides || {}); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (!employeeId) return null;

  const balances  = data?.balances  || [];
  const history   = data?.history   || [];

  const balanceChart = balances.map(b => ({
    type:      b.type,
    Remaining: Math.max(0, (b.total ?? 0) - (b.used ?? 0)),
    Used:      b.used ?? 0,
    color:     BALANCE_COLORS[b.type] || "#ffffff33",
  }));

  const addLeave = async () => {
    if (!addForm.from || !addForm.to) { setAddMsg({ type: "error", text: "From and To dates required." }); return; }
    setAddBusy(true); setAddMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/add-on-behalf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ employee_id: employeeId, ...addForm }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setAddMsg({ type: "success", text: "Leave added successfully." });
      setAddForm({ leave_type: "Annual", from: "", to: "", reason: "" });
      setShowAdd(false);
      // Reload
      const d = await fetch(`${API_BASE}/api/admin/leave-rules/employee/${employeeId}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      setData(d);
    } catch (e) { setAddMsg({ type: "error", text: e.message }); }
    finally { setAddBusy(false); }
  };

  const cancelLeave = async (id) => {
    try {
      await fetch(`${API_BASE}/api/admin/leave-rules/request/${id}/cancel`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      setData(d => ({ ...d, history: d.history.map(h => h.id === id ? { ...h, status: "CANCELLED" } : h) }));
    } catch {}
  };

  const saveOverrides = async () => {
    setSavingOvr(true); setOvrMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-rules/employee/${employeeId}/entitlement`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ overrides }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setOvrMsg("✓ Entitlement overrides saved.");
    } catch (e) { setOvrMsg(`⚠ ${e.message}`); }
    finally { setSavingOvr(false); }
  };

  return (
    <div className="fixed inset-0 z-[10060]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[500px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <p className="font-semibold text-white">{employeeName || employeeId}</p>
            <p className="text-xs text-[#00ff90]/50 mt-0.5">{employeeId} · Leave Management</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAdd(v => !v)}
              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
              <Plus size={12} /> Add Leave
            </button>
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Add leave form */}
        {showAdd && (
          <div className="px-5 py-4 border-b border-white/5 bg-[#00ff90]/3 space-y-3 shrink-0">
            <p className="text-xs text-[#00ff90]/60 uppercase tracking-wider">Add Leave on Behalf</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Type</label>
                <select className={inputCls} value={addForm.leave_type} onChange={e => setAddForm(f => ({ ...f, leave_type: e.target.value }))}>
                  {["Annual","Sick","Casual","Unpaid","Maternity","Paternity"].map(t => <option key={t}>{t}</option>)}
                </select></div>
              <div><label className={labelCls}>From</label>
                <input type="date" className={inputCls} value={addForm.from} onChange={e => setAddForm(f => ({ ...f, from: e.target.value }))} /></div>
              <div><label className={labelCls}>To</label>
                <input type="date" className={inputCls} value={addForm.to} onChange={e => setAddForm(f => ({ ...f, to: e.target.value }))} /></div>
              <div><label className={labelCls}>Reason</label>
                <input className={inputCls} value={addForm.reason} onChange={e => setAddForm(f => ({ ...f, reason: e.target.value }))} /></div>
            </div>
            {addMsg && <p className={`text-xs ${addMsg.type === "error" ? "text-red-400" : "text-[#00ff90]"}`}>{addMsg.text}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAdd(false)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
              <button onClick={addLeave} disabled={addBusy}
                className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                {addBusy ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 px-5 py-3 border-b border-white/5 shrink-0">
          {[["balances","Balances"],["history","History"],["entitlement","Entitlement"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                tab === k
                  ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                  : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
              }`}>{l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse" />)}</div>
          ) : (
            <>
              {/* Balances tab */}
              {tab === "balances" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {balances.map((b, i) => {
                      const color = BALANCE_COLORS[b.type] || "#ffffff44";
                      const rem   = (b.total ?? 0) - (b.used ?? 0);
                      const pct   = b.total > 0 ? Math.round((rem / b.total) * 100) : 0;
                      return (
                        <div key={i} className="rounded-lg border border-white/5 bg-white/3 p-3">
                          <p className="text-xs text-white/30 capitalize mb-1">{b.type}</p>
                          <p className="text-xl font-bold" style={{ color }}>{rem}</p>
                          <div className="h-1 rounded-full bg-white/5 mt-1.5">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <p className="text-xs text-white/20 mt-1">{b.used} used / {b.total} total</p>
                        </div>
                      );
                    })}
                  </div>

                  {balanceChart.length > 0 && (
                    <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                      <p className="text-xs text-white/30 uppercase tracking-wider mb-3">Remaining vs Used</p>
                      <ResponsiveContainer width="100%" height={100}>
                        <BarChart data={balanceChart} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                          <XAxis type="number" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                          <YAxis type="category" dataKey="type" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} width={55} />
                          <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v} days`, n]} />
                          <Bar dataKey="Remaining" stackId="a">
                            {balanceChart.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.7} />)}
                          </Bar>
                          <Bar dataKey="Used" stackId="a" fill="rgba(255,255,255,0.1)" radius={[0,3,3,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              )}

              {/* History tab */}
              {tab === "history" && (
                <div className="space-y-2">
                  {history.length === 0 ? (
                    <p className="text-white/20 text-sm italic text-center py-6">No leave history.</p>
                  ) : history.map((h, i) => {
                    const statusStyle = STATUS_STYLE[h.status] || STATUS_STYLE.PENDING;
                    const canCancel   = h.status === "APPROVED" || h.status === "PENDING";
                    return (
                      <div key={i} className="rounded-lg border border-white/5 bg-white/3 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm text-white/70 font-medium capitalize">{h.leave_type || h.type}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle}`}>{h.status}</span>
                            </div>
                            <p className="text-xs text-white/30">
                              {h.start_date || h.from} → {h.end_date || h.to}
                              {h.days != null && ` · ${h.days}d`}
                            </p>
                          </div>
                          {canCancel && (
                            <button onClick={() => cancelLeave(h.id)}
                              className="text-xs px-2 py-1 rounded-lg border border-white/10 text-white/25 hover:text-red-400 hover:border-red-500/20 transition-colors shrink-0">
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Entitlement overrides tab */}
              {tab === "entitlement" && (
                <div className="space-y-4">
                  <p className="text-xs text-white/30 leading-relaxed">
                    Override the standard entitlement for this employee. Leave blank to use org policy defaults.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {["Annual","Sick","Casual","Unpaid","Maternity","Paternity"].map(t => (
                      <div key={t}>
                        <label className={labelCls}>{t} (days/yr)</label>
                        <input type="number" step="1" className={inputCls}
                          value={overrides[t] ?? ""}
                          placeholder="Policy default"
                          onChange={e => setOverrides(o => ({ ...o, [t]: e.target.value ? Number(e.target.value) : undefined }))} />
                      </div>
                    ))}
                  </div>
                  {ovrMsg && <p className={`text-xs ${ovrMsg.startsWith("⚠") ? "text-red-400" : "text-[#00ff90]"}`}>{ovrMsg}</p>}
                  <button onClick={saveOverrides} disabled={savingOvr}
                    className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
                    {savingOvr ? "Saving…" : "Save Entitlement Overrides"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}